import { cn } from "@/lib/utils/cn";

/**
 * Força de senha por REGRA SIMPLES (sem zxcvbn — decisão D3 do épico hub#319):
 * conta critérios atendidos (comprimento + classes de caractere). 0–2 = fraca,
 * 3 = média, 4–5 = forte.
 */
export function passwordScore(password: string): number {
  if (!password) return 0;
  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  return Math.min(score, 5);
}

type Level = { label: string; bars: number; color: string };

function levelFor(score: number): Level {
  if (score <= 2) return { label: "Fraca", bars: 1, color: "bg-danger" };
  if (score === 3) return { label: "Média", bars: 2, color: "bg-warning" };
  return { label: "Forte", bars: 3, color: "bg-success" };
}

/** Gera uma senha forte (atende todos os critérios do medidor). */
export function generateStrongPassword(): string {
  const lower = "abcdefghijkmnpqrstuvwxyz";
  const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  const digits = "23456789";
  const symbols = "!@#$%&*?";
  const all = lower + upper + digits + symbols;
  const pick = (set: string) => set[Math.floor(Math.random() * set.length)];

  // Garante ao menos 1 de cada classe + 16 chars no total.
  const base = [pick(lower), pick(upper), pick(digits), pick(symbols)];
  while (base.length < 16) base.push(pick(all));

  // Embaralha (Fisher–Yates) para não fixar a ordem das classes.
  for (let i = base.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [base[i], base[j]] = [base[j], base[i]];
  }
  return base.join("");
}

export function PasswordStrength({ password }: { password: string }) {
  if (!password) return null;
  const level = levelFor(passwordScore(password));

  return (
    <div className="mt-1 flex items-center gap-2" aria-live="polite">
      <div className="flex flex-1 gap-1">
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className={cn(
              "h-1.5 flex-1 rounded-full transition-colors",
              i < level.bars ? level.color : "bg-border",
            )}
          />
        ))}
      </div>
      <span className="text-[11px] font-medium text-ink-soft">{level.label}</span>
    </div>
  );
}
